import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "primereact/button";
import { Splitter, SplitterPanel } from "primereact/splitter";
import { Panel } from "primereact/panel";

import OrderService from "../../services/order/order.service";
import Helpers from "../../utils/helpers";
import { usePersistentCountdown } from "../../hooks/usePersistentCountdown";

const PaymentConfirmDialog = ({
    showConfirmDialog,
    hideConfirmDialog,
    orderId,
    PlutusTransactionReferenceId,
    orderDetails,
    paymentModeType,
    amount
}: any) => {

    const [loading, setLoading] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState<any>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const [cancelMessage, setCancelMessage] = useState("");
    const [isTimeout, setIsTimeout] = useState(false);
    const [isPaymentMismatch, setIsPaymentMismatch] = useState(false);

    // ✅ browser-safe timers
    const intervalRef = useRef<number | null>(null);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // ✅ Persistent countdown
    const { timeLeft, clear: clearCountdown } = usePersistentCountdown(
        `payment-timer-${PlutusTransactionReferenceId}`,
        showConfirmDialog && loading,
        () => {
            stopPolling();
            setIsTimeout(true);
            setLoading(false);
        }
    );

    useEffect(() => {
        if (!showConfirmDialog || !PlutusTransactionReferenceId) return;

        stopPolling();
        setLoading(true);
        setIsTimeout(false);
        setIsPaymentMismatch(false);
        setPaymentDetails(null);

        intervalRef.current = window.setInterval(async () => {
            try {
                const response = await OrderService.getOnlinePaymenttransactionStatus(
                    PlutusTransactionReferenceId
                );
                if (response?.data?.isSuccess) {
                    let paidAmount = response?.data?.paymentAmount;
                    if (paidAmount && amount > 0 && paidAmount >= amount * 10) {
                        paidAmount = paidAmount / 100;
                    }
                    if (Math.abs(paidAmount - amount) > 0.1) {
                        setIsPaymentMismatch(true);
                    }
                    stopPolling();
                    clearCountdown();
                    setPaymentDetails(response.data);
                    setLoading(false);
                }
            } catch (error) {
                console.error("Payment status error:", error);
            }
        }, 2000);

        return () => stopPolling();
    }, [
        showConfirmDialog,
        PlutusTransactionReferenceId,
        stopPolling,
        clearCountdown
    ]);

    const handleCancel = async () => {
        setIsCancelling(true);
        stopPolling();
        clearCountdown();

        try {
            const response = await OrderService.cancelOnlineTransaction(PlutusTransactionReferenceId);
            if (response?.is === 1) {
                // Success
                setIsCancelled(true);
                setCancelMessage(response?.responseMessage || 'Transaction cancelled successfully');
                setLoading(false);
            } else {
                // Some other response
                setIsCancelled(true);
                setCancelMessage(response?.responseMessage || 'Transaction cancellation processed');
                setLoading(false);
            }
        } catch (error: any) {
            console.error("Error canceling transaction:", error);
            setIsCancelled(true);
            setCancelMessage(error?.response?.data?.responseMessage || 'Failed to cancel transaction');
            setLoading(false);
        } finally {
            setIsCancelling(false);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const renderFooter = () => {
        if (isTimeout || isCancelled) {
            return (
                <Button
                    label="Close"
                    icon="pi pi-times"
                    onClick={() => hideConfirmDialog(false)}
                    className="p-button-text p-button-secondary"
                />
            );
        }

        return (
            <div className="flex justify-content-end gap-2">
                {loading && !isCancelling ? (
                    <Button
                        label="Cancel Request"
                        icon="pi pi-stop-circle"
                        onClick={handleCancel}
                        className="p-button-danger p-button-text"
                        disabled={isCancelling}
                    />
                ) : isCancelling ? (
                    <Button
                        label="Cancelling..."
                        icon="pi pi-spin pi-spinner"
                        className="p-button-danger p-button-text"
                        disabled={true}
                    />
                ) : (
                    <Button
                        label="Close"
                        icon="pi pi-times"
                        onClick={() => hideConfirmDialog(!isPaymentMismatch)}
                        className="p-button-secondary p-button-text"
                    />
                )}
            </div>
        );
    };

    return (
        <Dialog
            visible={showConfirmDialog}
            header={
                <div className="flex align-items-center gap-2">
                    <i
                        className={
                            isCancelling
                                ? "pi pi-spin pi-spinner"
                                : isCancelled
                                    ? "pi pi-check-circle text-orange-500"
                                    : loading
                                        ? "pi pi-spin pi-spinner"
                                        : isTimeout
                                            ? "pi pi-exclamation-circle text-red-500"
                                            : isPaymentMismatch
                                                ? "pi pi-exclamation-triangle text-orange-500"
                                                : "pi pi-check-circle text-green-500"
                        }
                    />
                    <span>
                        {isCancelling
                            ? "Cancelling Transaction..."
                            : isCancelled
                                ? "Transaction Cancelled"
                                : loading
                                    ? "Waiting for Swipe Machine..."
                                    : isTimeout
                                        ? "Transaction Failed"
                                        : isPaymentMismatch
                                            ? "Payment Mismatch"
                                            : "Order Successfully Placed!"}
                    </span>
                </div>
            }
            className="custom-dialog"
            style={{ width: "50vw" }}
            modal
            onHide={() => {
                if (isCancelling) return;
                if (loading) {
                    handleCancel();
                } else {
                    hideConfirmDialog(!loading && !isCancelled && !isTimeout && !isPaymentMismatch);
                }
            }}
            footer={renderFooter()}
        >
            <div className="flex flex-column align-items-center justify-content-center min-h-12rem">
                {isCancelling ? (
                    <div className="text-center">
                        <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
                        <h3 className="mt-4">Cancelling Transaction Request</h3>
                        <p>Please wait while we cancel your transaction...</p>
                    </div>
                ) : isCancelled ? (
                    <div className="text-center">
                        <i className="pi pi-info-circle text-orange-500 text-4xl" />
                        <h3 className="mt-4 text-orange-600">Transaction Cancelled</h3>
                    </div>
                ) : loading ? (
                    <div className="text-center">
                        <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
                        <h3 className="mt-4">Payment in Progress</h3>

                        {paymentModeType === "UPI" ? (
                            <p>Please scan the QR code on the machine.</p>
                        ) : (
                            <p>Please swipe your card on the machine.</p>
                        )}

                        <div className="text-xl font-bold text-primary mt-2">
                            Time Remaining:
                            <span className={timeLeft < 30 ? "text-red-500 ml-2" : "ml-2"}>
                                {formatTime(timeLeft)}
                            </span>
                        </div>
                    </div>
                ) : isTimeout ? (
                    <div className="text-center">
                        <i className="pi pi-exclamation-triangle text-red-500 text-4xl" />
                        <h3 className="mt-4 text-red-500">Transaction Timeout</h3>
                        <p>No response received within 3 minutes.</p>
                    </div>
                ) : isPaymentMismatch ? (
                    <div className="text-center">
                        <i className="pi pi-exclamation-triangle text-orange-500 text-4xl" />
                        <h3 className="mt-4 text-orange-500">Payment Mismatch</h3>
                        <p>Order amount and paid amount mismatch. Please contact the admin.</p>
                    </div>
                ) : (
                    <div className="flex flex-column gap-3 w-full">
                        <div className="surface-card p-3 border-round border-1 border-200 flex justify-content-between">
                            <span className="font-bold text-xl">Order #: {orderId}</span>
                            <span className="text-green-600 font-bold">PAID SUCCESS</span>
                        </div>

                        <Splitter className="border-none">
                            <SplitterPanel className="pr-2">
                                <Panel header="Shipping Address">
                                    <div className="text-sm">
                                        {orderDetails?.address?.address1}{" "}
                                        {orderDetails?.address?.address2}{" "}
                                        {orderDetails?.address?.landmark},
                                        {orderDetails?.address?.cityName},{" "}
                                        {orderDetails?.address?.stateName},
                                        {orderDetails?.address?.zipcode}
                                    </div>
                                </Panel>
                            </SplitterPanel>

                            <SplitterPanel className="pl-2">
                                <Panel header="Order Summary">
                                    <div className="flex justify-content-between font-bold text-lg">
                                        <span>Total Paid:</span>
                                        <span className="text-green-600">
                                            {Helpers.formatAmountInINR(amount)}
                                        </span>
                                    </div>
                                </Panel>
                            </SplitterPanel>
                        </Splitter>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default PaymentConfirmDialog;
